<template>
  <div v-loading="isLoading" class="p-4 max-w-360 m-auto w-full">
    <div class="mx-auto grid w-full max-w-335 grid-cols-1 items-start gap-3 md:grid-cols-2">
      <KeyboardToggleSettings :settings="toggleSettings" @update:setting="setToggleSetting" />

      <div class="p-4 flex flex-col gap-4 glass-frosted rounded-8xl">
        <KeyboardReportRateSettings :model-value="reportRate" @update:model-value="setReportRate" />

        <KeyboardDebounceSettings
          :model-value="debounceLevel"
          @update:model-value="setDebounceLevel"
        />

        <KeyboardTickRateSettings :model-value="tickRate" @update:model-value="setTickRate" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useI18n } from 'vue-i18n'
  import KeyboardDebounceSettings from './components/KeyboardDebounceSettings.vue'
  import KeyboardReportRateSettings from './components/KeyboardReportRateSettings.vue'
  import KeyboardTickRateSettings from './components/KeyboardTickRateSettings.vue'
  import KeyboardToggleSettings from './components/KeyboardToggleSettings.vue'
  import { useKeyboardPerformance } from './composables/useKeyboardPerformance'
  import { useDeviceStore } from '@/stores'
  import type { KeyboardPerfCapability } from '@rapoo/device-sdk'
  import type { KeyboardPerformanceToggleSetting } from './types'

  const { t } = useI18n()

  const {
    isLoading,
    toggleValues,
    reportRate,
    debounceLevel,
    tickRate,
    setToggleSetting,
    setReportRate,
    setDebounceLevel,
    setTickRate,
  } = useKeyboardPerformance()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const supportsMacMode = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (capability): capability is KeyboardPerfCapability => capability.type === 'keyboardPerf',
    )
    return cap?.config.macMode === true
  })

  const toggleSettings = computed<KeyboardPerformanceToggleSetting[]>(() => [
    {
      id: 'winLock',
      title: t('performance.keyboard.toggles.winLock.title'),
      description: t('performance.keyboard.toggles.winLock.description'),
      value: toggleValues.winLock,
    },
    {
      id: 'fullAntiGhosting',
      title: t('performance.keyboard.toggles.fullAntiGhosting.title'),
      description: t('performance.keyboard.toggles.fullAntiGhosting.description'),
      value: toggleValues.fullAntiGhosting,
    },
    {
      id: 'adaptiveCalibration',
      title: t('performance.keyboard.toggles.adaptiveCalibration.title'),
      description: t('performance.keyboard.toggles.adaptiveCalibration.description'),
      value: toggleValues.adaptiveCalibration,
    },
    {
      id: 'bottomOutStabilization',
      title: t('performance.keyboard.toggles.bottomOutStabilization.title'),
      description: t('performance.keyboard.toggles.bottomOutStabilization.description'),
      value: toggleValues.bottomOutStabilization,
    },
    ...(supportsMacMode.value
      ? [
          {
            id: 'macMode' as const,
            title: t('performance.keyboard.toggles.macMode.title'),
            description: t('performance.keyboard.toggles.macMode.description'),
            value: toggleValues.macMode,
            compact: true,
          },
        ]
      : []),
    {
      id: 'swapWASD',
      title: t('performance.keyboard.toggles.swapWASD.title'),
      description: t('performance.keyboard.toggles.swapWASD.description'),
      value: toggleValues.swapWASD,
      compact: true,
    },
    {
      id: 'lockAltTab',
      title: t('performance.keyboard.toggles.lockAltTab.title'),
      description: t('performance.keyboard.toggles.lockAltTab.description'),
      value: toggleValues.lockAltTab,
      compact: true,
    },
    {
      id: 'lockAltF4',
      title: t('performance.keyboard.toggles.lockAltF4.title'),
      description: t('performance.keyboard.toggles.lockAltF4.description'),
      value: toggleValues.lockAltF4,
      compact: true,
    },
  ])
</script>
